import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Avatar, AvatarFallback, getInitials } from '../avatar';
import { Icon } from '../primitives/Icon';
import { AiAgentStar } from '../ai-agent-star';

export type ChatBubbleStatus = 'sent' | 'delivered' | 'read';

export interface ChatBubbleProps {
  message: ReactNode;
  /** Display name of the sender. Used for the avatar fallback initials and the sender label. */
  sender: string;
  /** Explicit speaker classification. Customers must leave this false or omitted. */
  isAi?: boolean;
  /** Free-form display string (e.g. "12:04" or "Yesterday"). The component does not format. */
  time?: string;
  /** When true, renders the bubble right-aligned in the brand color and omits the avatar. */
  isOwn?: boolean;
  /** Read receipt indicator — only rendered when `isOwn` is true. */
  status?: ChatBubbleStatus;
  tone?: 'accent' | 'neutral';
  className?: string;
}

/**
 * Single chat message bubble. Compose inside a flex column for thread
 * layout. `isOwn` flips alignment + accent; non-own bubbles render the
 * sender label and an avatar to the left.
 */
export function ChatBubble({
  message,
  sender,
  isAi = false,
  time,
  isOwn = false,
  status,
  tone = 'accent',
  className,
}: ChatBubbleProps) {
  const initials = getInitials(sender);
  return (
    <div
      className={cn(
        'flex w-full items-end gap-2',
        isOwn ? 'justify-end' : 'justify-start',
        className,
      )}
    >
      {!isOwn ? (
        <Avatar size="xs" className="h-[26px] w-[26px]">
          <AvatarFallback
            className={tone === 'neutral' ? 'bg-bg-elevated text-text-secondary' : undefined}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      ) : null}
      <div
        className={cn(
          'flex min-w-0 max-w-[75%] flex-col gap-1',
          isOwn ? 'items-end' : 'items-start',
        )}
      >
        {!isOwn || isAi ? (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-caption text-text-muted">
            <span className="truncate">{sender}</span>
            {isAi ? <AiAgentStar /> : null}
          </span>
        ) : null}
        <div
          data-slot="chat-bubble"
          className={cn(
            tone === 'neutral'
              ? 'rounded-lg px-3 py-2 text-[13px] leading-[1.65]'
              : 'rounded-2xl px-4 py-2 text-body shadow-sm',
            isOwn
              ? tone === 'neutral'
                ? 'border border-border bg-bg-elevated text-text-primary'
                : 'rounded-br-sm bg-accent-500 text-white'
              : tone === 'neutral'
                ? 'bg-bg-surface text-text-primary'
                : 'rounded-bl-sm bg-bg-elevated text-text-primary',
          )}
        >
          {message}
        </div>
        <div className="flex items-center gap-1.5 text-caption text-text-muted">
          {time ? <span>{time}</span> : null}
          {isOwn && status ? (
            <Icon
              name="check"
              size={11}
              className={status === 'read' ? 'text-secondary-500' : 'text-text-muted'}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
